import { useMemo } from "react";
import { logger } from "@/lib/logger";

export interface ParagraphItem {
  id: string;
  label: string;
}

export type ParagraphExtractionMode = "silver" | "consolidated";

const PARAGRAPH_LABEL_PATTERN = /^§\s*[\d]+[\wæøå]*(?:\s*[\wæøå]+)?\.?/i;

const extractSilverParagraphs = (rawHTML: string): ParagraphItem[] => {
  const container = document.createElement("div");
  container.innerHTML = rawHTML;

  const paragraphElements = container.querySelectorAll(".Paragraf");
  const items: ParagraphItem[] = [];

  paragraphElements.forEach((el) => {
    const id = el.id;
    if (!id) return;

    const paragrafNrSpan = el.querySelector(".ParagrafNr");
    if (!paragrafNrSpan) return;

    const labelText = paragrafNrSpan.textContent?.trim() || "";
    if (!labelText) return;

    const cleanLabel = labelText.replace(/\.$/, "").trim();
    items.push({ id, label: cleanLabel });
  });

  return items;
};

const extractConsolidatedParagraphs = (rawHTML: string): ParagraphItem[] => {
  const container = document.createElement("div");
  container.innerHTML = rawHTML;

  const paragraphElements = container.querySelectorAll(
    'div[data-container-type="PARAGRAF"][id]'
  );
  const items: ParagraphItem[] = [];

  paragraphElements.forEach((el) => {
    const id = el.id;
    if (!id) return;

    const text = (el.textContent || "").replace(/\s+/g, " ").trim();
    const match = text.match(PARAGRAPH_LABEL_PATTERN);
    const label = match?.[0]?.replace(/\.$/, "").trim() || id;

    items.push({ id, label });
  });

  return items;
};

/**
 * Extracts paragraph items (§) from Retsinformation silver HTML or consolidated
 * container-wrapped HTML from the Consolidation API.
 */
export const useParagraphExtraction = (
  rawHTML: string,
  isEnabled: boolean,
  mode: ParagraphExtractionMode = "silver"
): ParagraphItem[] => {
  return useMemo(() => {
    if (!isEnabled || !rawHTML) {
      return [];
    }

    try {
      return mode === "consolidated"
        ? extractConsolidatedParagraphs(rawHTML)
        : extractSilverParagraphs(rawHTML);
    } catch (error) {
      logger.warn("Failed to extract paragraphs from document HTML", {
        operation: "extractParagraphs",
        error,
        mode,
      });
      return [];
    }
  }, [rawHTML, isEnabled, mode]);
};

export {
  extractSilverParagraphs,
  extractConsolidatedParagraphs,
};
